import React, { useContext, useMemo, useState } from 'react';
import { createStore, StoreApi, useStore } from 'zustand';
import {
  getVisibleQuantity,
  initialState,
  quantityReducer,
  QuantityReducerAction,
  QuantityStateType,
} from '@/screens/new-order/hooks/use-quantity-reducer';
import { useShallow } from 'zustand/shallow';

interface NewOrderQuantityStore {
  /**
   * Dispatch a quantity reducer action. Stable across the store's lifetime,
   * so it can be passed to callbacks and effects without invalidating them.
   */
  dispatch: (action: QuantityReducerAction) => void;

  /**
   * The reducer state. Replaced wholesale on every dispatch, so subscribing
   * to it directly re-renders on every quantity change — prefer the per-item
   * selector hooks below.
   */
  quantityState: QuantityStateType;
}

export type NewOrderQuantityStoreApi = StoreApi<NewOrderQuantityStore>;

const createNewOrderQuantityStore = (
  value: QuantityStateType = initialState,
): NewOrderQuantityStoreApi =>
  createStore<NewOrderQuantityStore>()((set, get) => ({
    dispatch: (action) =>
      set({ quantityState: quantityReducer(get().quantityState, action) }),
    quantityState: value,
  }));

const NewOrderQuantityStoreContext =
  React.createContext<NewOrderQuantityStoreApi | null>(null);

interface Props {
  children?: React.ReactNode;
  /**
   * Initial reducer state, used to seed the store (primarily for tests).
   */
  value?: QuantityStateType;
}

/**
 * Provides an isolated quantity store instance to the component tree. Mounted
 * wherever NewOrderQuantityStateContextProvider used to be, so each order
 * surface (app stack, order inbounds, smart order) keeps its own quantities.
 */
export const NewOrderQuantityStoreProvider: React.FC<Props> = ({
  children,
  value,
}) => {
  const [store] = useState(() => createNewOrderQuantityStore(value));

  return (
    <NewOrderQuantityStoreContext.Provider value={store}>
      {children}
    </NewOrderQuantityStoreContext.Provider>
  );
};

/**
 * Subscribe to quantity store state with a selector.
 */
export const useNewOrderQuantityStore = <T,>(
  selector: (state: NewOrderQuantityStore) => T,
): T => {
  const store = useContext(NewOrderQuantityStoreContext);
  if (!store) {
    throw new Error(
      'useNewOrderQuantityStore must be used within a NewOrderQuantityStoreProvider',
    );
  }
  return useStore(store, selector);
};

/**
 * Returns the store instance so callers can read quantity state imperatively
 * (`store.getState().quantityState`) without subscribing. Use this for logic
 * that only needs the current value inside a callback — it never triggers a
 * re-render when quantities change.
 */
export const useNewOrderQuantityStoreApi = (): NewOrderQuantityStoreApi => {
  const store = useContext(NewOrderQuantityStoreContext);
  if (!store) {
    throw new Error(
      'useNewOrderQuantityStoreApi must be used within a NewOrderQuantityStoreProvider',
    );
  }
  return store;
};

export const useItemVisibleQuantity = (
  variantPackUUID?: string | null,
): number =>
  useNewOrderQuantityStore((s) =>
    variantPackUUID ? getVisibleQuantity(s.quantityState, variantPackUUID) : 0,
  );

/**
 * True when any entry in the merged quantity map is positive. Preserves the
 * order guide screen's existing merge semantics: saved values shadow local
 * ones for keys present in both maps.
 */
export const useHasAnyItemQuantity = (): boolean =>
  useNewOrderQuantityStore((s) =>
    Object.values({
      ...s.quantityState.localQuantityMap,
      ...s.quantityState.savedQuantityMap,
    }).some((quantity) => quantity > 0),
  );

/**
 * Builds a QuantityStateType scoped to one row's variant packs so existing
 * child props don't have to change. Subscribes only to the row's own local
 * and saved entries (shallow-compared), so the row re-renders only when one
 * of its own quantities changes.
 *
 * Ensure this is called with a stable reference to actually avoid unnecessary
 * re-renders.
 *
 * TODO: migrate consumers of this hook to just need a quantity map instead of
 * having to know that we strip out `eventPayloadMap` and `variantPackDetailMap`
 *
 * https://usepepper.atlassian.net/browse/STOR-1898
 */
export const useItemQuantityState = (
  variantPackUUIDs: string[],
): QuantityStateType => {
  const rowQuantities = useNewOrderQuantityStore(
    useShallow((s) =>
      variantPackUUIDs.flatMap((uuid) => [
        s.quantityState.localQuantityMap[uuid],
        s.quantityState.savedQuantityMap[uuid],
      ]),
    ),
  );

  return useMemo(() => {
    const rowState: QuantityStateType = {
      eventPayloadMap: {},
      localQuantityMap: {},
      savedQuantityMap: {},
      variantPackDetailMap: {},
    };
    variantPackUUIDs.forEach((uuid, index) => {
      const local = rowQuantities[index * 2];
      const saved = rowQuantities[index * 2 + 1];
      if (local !== undefined) {
        rowState.localQuantityMap[uuid] = local;
      }
      if (saved !== undefined) {
        rowState.savedQuantityMap[uuid] = saved;
      }
    });
    return rowState;
  }, [rowQuantities, variantPackUUIDs]);
};
